import React from 'react';
import { m, AnimatePresence } from 'framer-motion';

const VideoModal = ({ isOpen, onClose, videoUrl, title, description }) => {
  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  // Close on ESC key
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto overscroll-contain"
          onClick={onClose}
        >
          {/* Backdrop with gradient */}
          <div className="fixed inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-98" />

          {/* Scrollable Container */}
          <div className="relative z-10 w-full min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 py-16">
            {/* Modal Content */}
            <m.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-[95vw] lg:max-w-[1200px] xl:max-w-[1400px] mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button - Fixed position */}
              <button
                onClick={onClose}
                className="sticky top-4 left-full ml-4 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white hover:text-red-500 transition-all duration-300 z-30 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full backdrop-blur-sm mb-4 float-right"
                aria-label="Close video"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5 sm:h-6 sm:w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>

              {/* Title */}
              {title && (
                <m.h3 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-white text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-4 sm:mb-6 text-center px-4 drop-shadow-lg"
                >
                  {title}
                </m.h3>
              )}

              {/* Video Container with border and shadow */}
              <m.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="relative w-full bg-black rounded-xl overflow-hidden shadow-2xl ring-4 ring-white ring-opacity-10"
                style={{ paddingBottom: '56.25%' }}
              >
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                  title={title || 'Video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </m.div>

              {/* Description Card */}
              {description && (
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl shadow-xl mt-4 sm:mt-6 overflow-hidden"
                >
                  <div className="p-4 sm:p-6 md:p-8">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Info Icon */}
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#146084] to-[#0d4a5f] rounded-full flex items-center justify-center mt-1">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-4 w-4 sm:h-5 sm:w-5 text-white" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                      </div>
                      
                      {/* Description Text */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[#146084] font-semibold text-base sm:text-lg mb-2">
                          About this video
                        </h4>
                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                </m.div>
              )}

              {/* Keyboard Hint */}
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center mt-4 sm:mt-6 mb-4"
              >
                <p className="text-white text-xs sm:text-sm opacity-60 flex items-center justify-center gap-2 flex-wrap">
                  <span className="hidden sm:inline">Press</span>
                  <kbd className="px-2 py-1 bg-white bg-opacity-10 rounded text-xs font-mono">ESC</kbd>
                  <span className="hidden sm:inline">or click outside to close</span>
                  <span className="sm:hidden">to close</span>
                </p>
              </m.div>
            </m.div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;